
import { v4 as uuidv4 } from "uuid";
import { ModelBase } from "@models/UpsertableBase";
import { DataType } from "@dndModels/Types";

/**
 * This is used for creating starting equipment for new 
 * characters.  
 * StartItems go on GameClass and on StartEquipment.
 */
export class StartEquipmentChoice {
  id: string;
  options: StartEquipmentOption[];
  selected_option: string;

  constructor(obj?: any) {
    this.id = obj && obj.id ? `${obj.id}` : uuidv4().toString();
    this.options = [];
    if (obj && obj.options) {
      obj.options.forEach((opt: any) => {
        const option = new StartEquipmentOption(opt);
        this.options.push(option);
      });
    }
    this.selected_option = this.options.length === 1 ? this.options[0].id : "";
  }

  toDBObj(): any {
    const options: any[] = [];
    this.options.forEach(opt => {
      options.push(opt.toDBObj());
    });
    return {
      options
    };
  }

  clone(drop_ids = false): StartEquipmentChoice {
    const new_obj = new StartEquipmentChoice();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: StartEquipmentChoice, drop_ids = false): void {
    if (!drop_ids) {
      this.id = copyMe.id;
    }
    this.options = [];
    copyMe.options.forEach(opt => {
      this.options.push(opt.clone(drop_ids));
    });
    this.selected_option = copyMe.selected_option;
  }
}

export class StartEquipmentOption {
  id: string;
  items: StartEquipmentItem[];

  constructor(obj?: any) {
    this.id = obj && obj.id ? `${obj.id}` : uuidv4().toString();
    this.items = [];
    if (obj && obj.items) {
      obj.items.forEach((i: any) => {
        const item = new StartEquipmentItem(i);
        this.items.push(item);
      });
    }
  }

  toDBObj(): any {
    return {
      items: this.items
    };
  }

  clone(drop_ids = false): StartEquipmentOption {
    const new_obj = new StartEquipmentOption();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: StartEquipmentOption, drop_ids = false): void {
    if (!drop_ids) {
      this.id = copyMe.id;
    }
    this.items = [];
    copyMe.items.forEach(item => {
      this.items.push(item.clone(drop_ids));
    });
  }
}

export class StartEquipmentItem {
  id: string;
  item_type: "Item" | "Type" | "Pack" | "Money" | "Other Item" = "Item"; // Item, Item Type, Pack, Money
  item: string;
  detail: string;
  item_count: number;
  selected_item_id: string;

  constructor(obj?: any) {
    this.id = obj && obj.id ? `${obj.id}` : uuidv4().toString();
    this.item_type = obj ? obj.item_type : "Item";
    this.item = obj ? obj.item : "";
    this.item_count = obj && obj.item_count ? obj.item_count : 1;

    if (this.item_type === "Money" && !["platinum","gold","electrum","silver","copper"].includes(this.item.toLowerCase())) {
      // it's the old way.  Convert it.
      
      const money_split = this.item.toLowerCase().split(" ");
      this.item_count = +money_split[0];
      switch (money_split[1]) {
        case "pp": this.item = "Platinum"; break;
        case "gp": this.item = "Gold"; break;
        case "ep": this.item = "Electrum"; break;
        case "sp": this.item = "Silver"; break;
        case "cp": this.item = "Copper"; break;
      }
    }
    this.detail = obj && obj.detail ? obj.detail : "";
    this.selected_item_id = "";
  }

  toDBObj(): any {
    return {
      item_type: this.item_type,
      item: this.item,
      item_count: this.item_count,
      detail: this.detail
    };
  }

  clone(drop_ids = false): StartEquipmentItem {
    const new_obj = new StartEquipmentItem();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: StartEquipmentItem, drop_ids = false): void {
    if (!drop_ids) {
      this.id = copyMe.id;
    }
    this.detail = copyMe.detail;
    this.item = copyMe.item;
    this.item_count = copyMe.item_count;
    this.item_type = copyMe.item_type;
    this.selected_item_id = copyMe.selected_item_id;
  }
}

export class EquipmentPack extends ModelBase {
  data_type: DataType = "equipment_pack";
  cost = 0;
  items: EquipmentPackItem[] = [];
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.cost = 0;
      if (obj.cost) {
        if (typeof(obj.cost) === "string") {
          const pieces = obj.cost.split(" ");
          this.cost = +pieces[0];
        } else {
          this.cost = +obj.cost;
        }
      }
      this.items = [];
      if (obj.items) {
        obj.items.forEach((o: any) => {
          const item = new EquipmentPackItem(o);
          this.items.push(item);
        });
      }
    }
    this.loaded = true;
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      cost: this.cost,
      items: this.items.map(o => o.toDBObj())
    };
  }

  toLight(): any {
    return this.toDBObj();
  }

  clone(drop_ids = false): EquipmentPack {
    const new_obj = new EquipmentPack();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: EquipmentPack, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.cost = copyMe.cost;
    this.items = [...copyMe.items];
  }
}

export class EquipmentPackItem {
  id: string;
  item_id: string; // items, type, pack, money
  count: number;

  constructor(obj?: any) {
    this.id = obj && obj.id ? `${obj.id}` : uuidv4().toString();
    this.item_id = obj ? obj.item_id : "";
    this.count = obj ? obj.count : 1;
  }

  toDBObj(): any {
    return {
      item_id: this.item_id,
      count: this.count
    };
  }

  clone(drop_ids = false): EquipmentPackItem {
    const new_obj = new EquipmentPackItem();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: EquipmentPackItem, drop_ids = false): void {
    if (!drop_ids) {
      this.id = copyMe.id;
    }
    this.item_id = copyMe.item_id;
    this.count = copyMe.count;
  }
}
